import { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import type { Address } from "viem";

import { isDevelopment } from "config/env";
import { PoolsDetailsContextProvider } from "features/pools/context/PoolsDetailsContext/PoolsDetailsContext";
import { SyntheticsStateContextProvider } from "context/SyntheticsStateContext/SyntheticsStateContextProvider";
import { X10000StateProvider } from "@/modules/cex/store/X10000StateContext/X10000StateContext";
import { useChainId } from "lib/chains";
import { buildAccountDashboardUrl } from "shared/utils/buildAccountDashboardUrl";
import { RedirectWithQuery } from "shared/components/RedirectWithQuery/RedirectWithQuery";
import { Trans } from "@lingui/macro";

// Lazy imports - 使用命名导出转换
const LandingPage = lazy(() => import("@/modules/landing/components/LandingPage/LandingPage"));
const LandingPageV2 = lazy(() => import("@/modules/landing/components/LandingPageV2/LandingPageV2"));
const Syntheticsx10000Page = lazy(() => import("@/modules/cex/features/x10000trade/pages/Syntheticsx10000Page/Syntheticsx10000Page").then((m) => ({ default: m.Syntheticsx10000Page })));
const DashboardV2 = lazy(() => import("features/stats/pages/Dashboard/DashboardV2"));
const Stats = lazy(() => import("features/stats/pages/Stats/Stats"));
const SyntheticsStats = lazy(() => import("features/stats/pages/SyntheticsStats/SyntheticsStats").then((m) => ({ default: m.SyntheticsStats })));
const Pools = lazy(() => import("features/pools/pages/Pools/Pools"));
const PoolsDetails = lazy(() => import("features/pools/pages/PoolsDetails/PoolsDetails").then((m) => ({ default: m.PoolsDetails })));
const Referrals = lazy(() => import("features/referrals/pages/Referrals/Referrals"));
const ReferralsTier = lazy(() => import("features/referrals/pages/ReferralsTier/ReferralsTier"));
const LeaderboardPage = lazy(() => import("features/leaderboard/pages/LeaderboardPage/LeaderboardPage").then((m) => ({ default: m.LeaderboardPage })));
const CompetitionRedirect = lazy(() => import("features/leaderboard/pages/LeaderboardPage/LeaderboardPage").then((m) => ({ default: m.CompetitionRedirect })));
const Earn = lazy(() => import("features/earn/pages/Earn/Earn").then((m) => ({ default: m.default })));
const PointsPage = lazy(() => import("features/points/pages/PointsPage/PointsPage").then((m) => ({ default: m.PointsPage })));
const AccountDashboard = lazy(() => import("features/accounts/pages/AccountDashboard/AccountDashboard").then((m) => ({ default: m.AccountDashboard })));
const AccountsRouter = lazy(() => import("features/accounts/pages/Actions/ActionsRouter").then((m) => ({ default: m.AccountsRouter })));
const BeginAccountTransfer = lazy(() => import("features/accounts/pages/AccountTransfer/BeginAccountTransfer/BeginAccountTransfer"));
const CompleteAccountTransfer = lazy(() => import("features/accounts/pages/AccountTransfer/CompleteAccountTransfer/CompleteAccountTransfer"));
const Jobs = lazy(() => import("features/common/pages/Jobs/Jobs"));
const BuyGMX = lazy(() => import("features/common/pages/BuyGMX/BuyGMX"));
const NftWallet = lazy(() => import("features/common/pages/NftWallet/NftWallet"));
const PageNotFound = lazy(() => import("features/common/pages/PageNotFound/PageNotFound"));
const UiPage = lazy(() => import("features/common/pages/UiPage/UiPage"));
const ParseTransactionPage = lazy(() => import("features/common/pages/ParseTransaction/ParseTransaction").then((m) => ({ default: m.ParseTransactionPage })));
const PriceImpactRebatesStatsPage = lazy(() => import("features/common/pages/PriceImpactRebatesStats/PriceImpactRebatesStats").then((m) => ({ default: m.PriceImpactRebatesStatsPage })));
const TestPermits = lazy(() => import("features/common/pages/TestPermits/TestPermits").then((m) => ({ default: m.TestPermits })));

import Loader from "components/Loader/Loader";

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="flex h-screen items-center justify-center"><Loader /></div>}>
    {children}
  </Suspense>
);

export function MainRoutes({ openSettings }: { openSettings: () => void }) {
  const { chainId } = useChainId();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Switch>
      {/* Redirects */}
      <Redirect exact from="/actions/v2" to="/accounts" />
      <Redirect exact from="/actions" to="/accounts" />
      <Redirect exact from="/actions/:account" to="/accounts/:account" />
      <RedirectWithQuery exact from="/dashboard" to="/stats" />
      <RedirectWithQuery exact from="/monitor/v2" to="/monitor" />
      <Route exact path="/v1">
        <RedirectWithQuery to="/trade" />
      </Route>
      <Route exact path="/actions/:v/:account">
        {({ match }) => (
          <Redirect
            to={buildAccountDashboardUrl(match?.params.account as Address, chainId, match?.params.v === "v1" ? 1 : 2)}
          />
        )}
      </Route>

      {/* Landing V2 - 新首页作为默认首页 */}
      <Route exact path="/">
        <SuspenseWrapper>
          <LandingPageV2 />
        </SuspenseWrapper>
      </Route>

      {/* Landing - 原首页移动到 /v2 */}
      <Route exact path="/v2">
        <SuspenseWrapper>
          <LandingPage />
        </SuspenseWrapper>
      </Route>

      {/* Trade */}
      <Route exact path="/trade/:tradeType?">
        <X10000StateProvider>
          <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="x10000trade">
            <SuspenseWrapper>
              <Syntheticsx10000Page openSettings={openSettings} />
            </SuspenseWrapper>
          </SyntheticsStateContextProvider>
        </X10000StateProvider>
      </Route>

      {/* Stats */}
      <Route exact path="/stats">
        <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="stats">
          <SuspenseWrapper>
            <DashboardV2 />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>
      <Route exact path="/monitor/v1">
        <SuspenseWrapper>
          <Stats />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/monitor">
        <SuspenseWrapper>
          <SyntheticsStats />
        </SuspenseWrapper>
      </Route>

      {/* Pools */}
      <Route exact path="/pools">
        <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="pools">
          <SuspenseWrapper>
            <Pools />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>
      <Route exact path="/pools/details">
        <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="pools">
          <PoolsDetailsContextProvider>
            <SuspenseWrapper>
              <PoolsDetails />
            </SuspenseWrapper>
          </PoolsDetailsContextProvider>
        </SyntheticsStateContextProvider>
      </Route>

      {/* Referrals */}
      <Route exact path="/referrals">
        <SuspenseWrapper>
          <Referrals />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/referrals/:account">
        <SuspenseWrapper>
          <Referrals />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/referrals-tier">
        <SuspenseWrapper>
          <ReferralsTier />
        </SuspenseWrapper>
      </Route>

      {/* Earn */}
      <Route exact path="/earn">
        <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="earn">
          <SuspenseWrapper>
            <Earn />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>

      {/* Points */}
      <Route exact path="/points">
        <SuspenseWrapper>
          <PointsPage />
        </SuspenseWrapper>
      </Route>

      {/* Leaderboard */}
      <Route path="/leaderboard/">
        <SyntheticsStateContextProvider skipLocalReferralCode pageType="leaderboard">
          <SuspenseWrapper>
            <LeaderboardPage />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>
      <Route exact path="/competitions/">
        <SyntheticsStateContextProvider skipLocalReferralCode pageType="competitions">
          <SuspenseWrapper>
            <CompetitionRedirect />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>
      <Route path="/competitions/:leaderboardPageKey">
        <SyntheticsStateContextProvider skipLocalReferralCode pageType="competitions">
          <SuspenseWrapper>
            <LeaderboardPage />
          </SuspenseWrapper>
        </SyntheticsStateContextProvider>
      </Route>

      {/* Accounts */}
      <Route exact path="/accounts">
        <SuspenseWrapper>
          <AccountsRouter />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/accounts/:account">
        <SuspenseWrapper>
          <AccountDashboard />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/begin_account_transfer">
        <SuspenseWrapper>
          <BeginAccountTransfer />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/complete_account_transfer/:sender/:receiver">
        <SuspenseWrapper>
          <CompleteAccountTransfer />
        </SuspenseWrapper>
      </Route>

      {/* Common */}
      <Route exact path="/price_impact_rebates_stats">
        <SuspenseWrapper>
          <PriceImpactRebatesStatsPage />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/jobs">
        <SuspenseWrapper>
          <Jobs />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/buy_gmx">
        <SuspenseWrapper>
          <BuyGMX />
        </SuspenseWrapper>
      </Route>
      <Route exact path="/nft_wallet">
        <SuspenseWrapper>
          <NftWallet />
        </SuspenseWrapper>
      </Route>
      <Route path="/parsetx/:network/:tx">
        <SuspenseWrapper>
          <ParseTransactionPage />
        </SuspenseWrapper>
      </Route>
      {isDevelopment() && (
        <>
          <Route exact path="/ui">
            <SuspenseWrapper>
              <UiPage />
            </SuspenseWrapper>
          </Route>
          <Route exact path="/permits">
            <SuspenseWrapper>
              <TestPermits />
            </SuspenseWrapper>
          </Route>
        </>
      )}
      <Route path="*">
        <SuspenseWrapper>
          <PageNotFound />
        </SuspenseWrapper>
      </Route>
    </Switch>
  );
}
